import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const DATA_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const STOCK_DETAIL_PATH = "/stocks2";

// Empty default state: user chooses stocks and metrics themselves.
const DEFAULT_TICKERS = [""];
const DEFAULT_VISIBLE_METRICS = [];

const METRICS = [
  { key: "price", label: "Price", group: "Basics", type: "money" },
  { key: "marketCap", label: "Market Cap", group: "Basics", type: "moneyLarge" },
  { key: "enterpriseValue", label: "Enterprise Value", group: "Basics", type: "moneyLarge" },

  { key: "forwardPE", label: "Forward P/E", group: "Valuation", type: "ratio" },
  { key: "peg", label: "PEG", group: "Valuation", type: "ratio" },
  { key: "pb", label: "P/B", group: "Valuation", type: "ratio" },
  { key: "ptbv", label: "P/TBV", group: "Valuation", type: "ratio" },
  { key: "evMarketCap", label: "EV / Market Cap", group: "Valuation", type: "ratio" },

  { key: "growthRate", label: "Growth Rate", group: "Quality", type: "percent" },
  { key: "grossMargin", label: "Gross Margin", group: "Quality", type: "percent" },
  { key: "profitMargin", label: "Profit Margin", group: "Quality", type: "percent" },
  { key: "currentRatio", label: "Current Ratio", group: "Quality", type: "ratio" },
  { key: "buybackYield", label: "Buyback Yield", group: "Quality", type: "percent" },
  { key: "institutionalOwnership", label: "Institutional Ownership", group: "Quality", type: "percent" },

  { key: "rsi14", label: "RSI", group: "Technical", type: "ratio" },
  { key: "beta", label: "Beta", group: "Technical", type: "ratio" },
  { key: "pctAbove52WeekLow", label: "% Above 52W Low", group: "Technical", type: "percent" },
];

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[$,%]/g, "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function normalizeTicker(value) {
  return String(value || "").trim().toUpperCase().replace(/\s+/g, "");
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function formatLargeMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  const abs = Math.abs(n);
  if (abs >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(2)}T`;
  if (abs >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
}

function formatPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function formatRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}

function formatValue(value, type) {
  if (value === null || value === undefined || value === "" || Number.isNaN(value)) return "—";
  if (type === "money") return formatMoney(value);
  if (type === "moneyLarge") return formatLargeMoney(value);
  if (type === "percent") return formatPercent(value);
  if (type === "ratio") return formatRatio(value);
  return String(value);
}

function getMetricValue(row, key) {
  if (!row) return null;

  if (key === "evMarketCap") {
    const ev = parseNumber(row.enterpriseValue);
    const marketCap = parseNumber(row.marketCap);

    if (!Number.isFinite(ev) || !Number.isFinite(marketCap) || marketCap <= 0) {
      return null;
    }

    return ev / marketCap;
  }

  return row[key];
}

function groupMetrics(metrics) {
  return metrics.reduce((acc, metric) => {
    if (!acc[metric.group]) acc[metric.group] = [];
    acc[metric.group].push(metric);
    return acc;
  }, {});
}

export default function StockMetricComparisonPage() {
  const [allRows, setAllRows] = useState([]);
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [tickerInputs, setTickerInputs] = useState(DEFAULT_TICKERS);
  const [visibleMetricKeys, setVisibleMetricKeys] = useState(DEFAULT_VISIBLE_METRICS);
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(DATA_URL, { cache: "no-store" });
        const text = await res.text();

        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        if (text.trim().startsWith("<")) {
          throw new Error("Expected JSON but received HTML. Check the data URL/path.");
        }

        const json = JSON.parse(text);
        setAllRows(Array.isArray(json.data) ? json.data : []);
        setAsOf(json.as_of || null);
      } catch (e) {
        console.error(e);
        setError(e.message || "Failed to load comparison data.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  const rowsByTicker = useMemo(() => {
    const map = new Map();

    for (const row of allRows) {
      const ticker = normalizeTicker(row.ticker);
      if (ticker) map.set(ticker, row);
    }

    return map;
  }, [allRows]);

  const selectedMetrics = useMemo(() => {
    return METRICS.filter((metric) => visibleMetricKeys.includes(metric.key));
  }, [visibleMetricKeys]);

  const comparisonRows = useMemo(() => {
    const rows = tickerInputs.map((rawTicker, index) => {
      const ticker = normalizeTicker(rawTicker);
      const data = ticker ? rowsByTicker.get(ticker) : null;

      return {
        id: `${index}-${ticker || "blank"}`,
        originalIndex: index,
        inputTicker: rawTicker,
        ticker,
        data,
        found: Boolean(data),
      };
    });

    if (!sortKey) return rows;

    return [...rows].sort((a, b) => {
      const av = getMetricValue(a.data, sortKey);
      const bv = getMetricValue(b.data, sortKey);

      const aMissing = av === null || av === undefined || av === "";
      const bMissing = bv === null || bv === undefined || bv === "";

      if (aMissing && bMissing) return a.originalIndex - b.originalIndex;
      if (aMissing) return 1;
      if (bMissing) return -1;

      const an = Number(av);
      const bn = Number(bv);

      if (Number.isFinite(an) && Number.isFinite(bn)) {
        return sortDir === "asc" ? an - bn : bn - an;
      }

      const cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [tickerInputs, rowsByTicker, sortKey, sortDir]);

  const metricGroups = useMemo(() => groupMetrics(METRICS), []);

  function toggleMetric(metricKey) {
    setVisibleMetricKeys((prev) => {
      if (prev.includes(metricKey)) {
        if (sortKey === metricKey) setSortKey("");
        return prev.filter((key) => key !== metricKey);
      }

      return [...prev, metricKey];
    });
  }

  function selectGroup(groupName) {
    const groupKeys = METRICS.filter((metric) => metric.group === groupName).map(
      (metric) => metric.key
    );

    setVisibleMetricKeys((prev) => {
      const hasAll = groupKeys.every((key) => prev.includes(key));

      if (hasAll) {
        if (groupKeys.includes(sortKey)) setSortKey("");
        return prev.filter((key) => !groupKeys.includes(key));
      }

      return Array.from(new Set([...prev, ...groupKeys]));
    });
  }

  function updateTicker(index, value) {
    setTickerInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function addTickerRow() {
    setTickerInputs((prev) => [...prev, ""]);
  }

  function removeTickerRow(index) {
    setTickerInputs((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [""];
    });
  }

  function clearTickers() {
    setTickerInputs([""]);
    setSortKey("");
    setSortDir("desc");
  }

  function resetTickers() {
    setTickerInputs(DEFAULT_TICKERS);
    setSortKey("");
    setSortDir("desc");
  }

  function resetMetrics() {
    setVisibleMetricKeys(DEFAULT_VISIBLE_METRICS);
    setSortKey("");
    setSortDir("desc");
  }

  function selectAllMetrics() {
    setVisibleMetricKeys(METRICS.map((metric) => metric.key));
  }

  function clearMetrics() {
    setVisibleMetricKeys([]);
    setSortKey("");
  }

  function handleSort(metricKey) {
    if (sortKey === metricKey) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(metricKey);
      setSortDir("desc");
    }
  }

  return (
    <div style={pageShell}>
      <section style={heroCard}>
        <div>
          <div style={eyebrow}>Majorah Compare</div>
          <h1 style={title}>Stock Metric Comparison</h1>
          <p style={subtitle}>
            Add tickers on the left, then toggle the metric bubbles to compare
            valuation, quality, technical, and balance sheet metrics.
            {asOf ? ` Data as of ${asOf}.` : ""}
          </p>
        </div>

        <div style={heroActions}>
          <button type="button" onClick={resetTickers} style={darkButton}>
            Reset Tickers
          </button>
          <button type="button" onClick={clearTickers} style={lightButton}>
            Clear Tickers
          </button>
        </div>
      </section>

      <section style={metricPickerCard}>
        <div style={pickerHeader}>
          <div>
            <h2 style={sectionTitle}>Metric Columns</h2>
            <p style={sectionSubtitle}>
              Selected metrics appear as table columns. Click any bubble to toggle it.
            </p>
          </div>

          <div style={pickerActions}>
            <button type="button" onClick={selectAllMetrics} style={smallButton}>
              Select All
            </button>
            <button type="button" onClick={clearMetrics} style={smallButton}>
              Clear
            </button>
            <button type="button" onClick={resetMetrics} style={smallButtonPrimary}>
              Default
            </button>
          </div>
        </div>

        <div style={groupsWrap}>
          {Object.entries(metricGroups).map(([groupName, metrics]) => {
            const groupKeys = metrics.map((metric) => metric.key);
            const activeCount = groupKeys.filter((key) =>
              visibleMetricKeys.includes(key)
            ).length;

            return (
              <div key={groupName} style={metricGroup}>
                <button
                  type="button"
                  onClick={() => selectGroup(groupName)}
                  style={groupPill(activeCount === groupKeys.length)}
                  title="Toggle this whole group"
                >
                  {groupName} {activeCount}/{groupKeys.length}
                </button>

                <div style={metricBubbleRow}>
                  {metrics.map((metric) => {
                    const selected = visibleMetricKeys.includes(metric.key);

                    return (
                      <button
                        key={metric.key}
                        type="button"
                        onClick={() => toggleMetric(metric.key)}
                        style={metricBubble(selected)}
                      >
                        {metric.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {loading ? (
        <div style={message}>Loading stock metrics...</div>
      ) : error ? (
        <div style={errorBox}>{error}</div>
      ) : (
        <section style={tableCard}>
          <div style={tableToolbar}>
            <div>
              <h2 style={sectionTitle}>Comparison Table</h2>
              <p style={sectionSubtitle}>
                {tickerInputs.filter((t) => normalizeTicker(t)).length.toLocaleString()} tickers ·{" "}
                {selectedMetrics.length.toLocaleString()} selected metrics
              </p>
            </div>

            <button type="button" onClick={addTickerRow} style={addButton}>
              + Add Ticker
            </button>
          </div>

          <div style={tableWrap}>
            <table
              style={{
                ...table,
                minWidth: Math.max(820, 380 + selectedMetrics.length * 132),
              }}
            >
              <thead>
                <tr>
                  <th style={tickerHeaderCell}>Ticker</th>
                  <th style={nameHeaderCell}>Company</th>
                  {selectedMetrics.map((metric) => (
                    <th
                      key={metric.key}
                      onClick={() => handleSort(metric.key)}
                      style={metricHeaderCell}
                    >
                      {metric.label}
                      {sortKey === metric.key ? (
                        <span style={sortIndicator}>
                          {sortDir === "asc" ? " ▲" : " ▼"}
                        </span>
                      ) : null}
                    </th>
                  ))}
                  <th style={actionHeaderCell}>Remove</th>
                </tr>
              </thead>

              <tbody>
                {comparisonRows.map((row, displayIndex) => {
                  const { originalIndex, inputTicker, ticker, data, found } = row;

                  return (
                    <tr
                      key={`ticker-row-${originalIndex}`}
                      style={displayIndex % 2 === 0 ? rowEven : rowOdd}
                    >
                      <td style={tickerInputCell}>
                        <input
                          value={inputTicker}
                          onChange={(e) => updateTicker(originalIndex, e.target.value)}
                          placeholder="Ticker"
                          style={{
                            ...tickerInput,
                            borderColor: ticker && !found ? "#fecdd3" : "#cbd5e1",
                            background: ticker && !found ? "#fff1f2" : "#ffffff",
                          }}
                        />
                      </td>

                      <td style={nameCell}>
                        {ticker && found ? (
                          <div>
                            <Link
                              to={`${STOCK_DETAIL_PATH}/${encodeURIComponent(ticker)}`}
                              style={tickerLink}
                            >
                              {ticker}
                            </Link>
                            <div style={companyName}>{data.name || "—"}</div>
                          </div>
                        ) : ticker ? (
                          <span style={missingText}>Not found</span>
                        ) : (
                          <span style={mutedText}>Enter ticker</span>
                        )}
                      </td>

                      {selectedMetrics.map((metric) => (
                        <td key={metric.key} style={metricCell}>
                          {formatValue(getMetricValue(data, metric.key), metric.type)}
                        </td>
                      ))}

                      <td style={actionCell}>
                        <button
                          type="button"
                          onClick={() => removeTickerRow(originalIndex)}
                          style={removeButton}
                          aria-label={`Remove ticker row ${originalIndex + 1}`}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={noteBox}>
            Metrics populate from <code>stock_screener.json</code>. If a ticker shows
            “Not found,” it is not present in that dataset or the symbol format differs.
          </div>
        </section>
      )}
    </div>
  );
}

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "22px 10px 34px",
  fontFamily:
    "Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 22,
  background:
    "radial-gradient(circle at 16% 12%, rgba(56, 189, 248, 0.23), transparent 32%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  borderRadius: 24,
  boxShadow: "0 22px 52px rgba(15, 23, 42, 0.2)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  flexWrap: "wrap",
};

const eyebrow = {
  marginBottom: 8,
  color: "#93c5fd",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 900,
};

const title = {
  margin: 0,
  fontSize: 32,
  letterSpacing: "-0.05em",
  lineHeight: 1.02,
};

const subtitle = {
  maxWidth: 980,
  marginTop: 10,
  color: "#cbd5e1",
  lineHeight: 1.65,
  fontSize: 14,
};

const heroActions = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const darkButton = {
  padding: "10px 13px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.18)",
  background: "rgba(255,255,255,0.12)",
  color: "white",
  fontSize: 12,
  fontWeight: 900,
  cursor: "pointer",
};

const lightButton = {
  ...darkButton,
  background: "rgba(255,255,255,0.92)",
  color: "#0f172a",
};

const metricPickerCard = {
  maxWidth: 1680,
  margin: "0 auto 18px",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "rgba(255, 255, 255, 0.92)",
  boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
};

const pickerHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 14,
};

const pickerActions = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const sectionTitle = {
  margin: 0,
  fontSize: 17,
  letterSpacing: "-0.03em",
};

const sectionSubtitle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: 12,
};

const smallButton = {
  padding: "8px 11px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "white",
  color: "#334155",
  fontSize: 12,
  fontWeight: 850,
  cursor: "pointer",
};

const smallButtonPrimary = {
  ...smallButton,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1d4ed8",
};

const groupsWrap = {
  display: "grid",
  gap: 12,
};

const metricGroup = {
  display: "grid",
  gridTemplateColumns: "150px 1fr",
  gap: 12,
  alignItems: "start",
};

const metricBubbleRow = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

function groupPill(active) {
  return {
    width: "100%",
    textAlign: "left",
    padding: "9px 11px",
    borderRadius: 999,
    border: active ? "1px solid #93c5fd" : "1px solid #e2e8f0",
    background: active ? "#eff6ff" : "#f8fafc",
    color: active ? "#1d4ed8" : "#475569",
    fontSize: 12,
    fontWeight: 950,
    cursor: "pointer",
  };
}

function metricBubble(selected) {
  return {
    padding: "8px 11px",
    borderRadius: 999,
    border: selected ? "1px solid #93c5fd" : "1px solid #e2e8f0",
    background: selected ? "#eff6ff" : "white",
    color: selected ? "#1d4ed8" : "#475569",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    boxShadow: selected ? "0 6px 14px rgba(37, 99, 235, 0.12)" : "none",
  };
}

const tableCard = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 18,
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  background: "white",
  boxShadow: "0 16px 36px rgba(15, 23, 42, 0.1)",
};

const tableToolbar = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 14,
};

const addButton = {
  padding: "10px 13px",
  borderRadius: 14,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1d4ed8",
  fontSize: 12,
  fontWeight: 950,
  cursor: "pointer",
};

const tableWrap = {
  overflowX: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  maxHeight: "76vh",
};

const table = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const headerBase = {
  padding: "9px 9px",
  fontSize: 10,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  background: "#f8fafc",
  whiteSpace: "nowrap",
  cursor: "pointer",
  position: "sticky",
  top: 0,
  zIndex: 6,
  fontWeight: 950,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
};

const tickerHeaderCell = {
  ...headerBase,
  left: 0,
  zIndex: 9,
  minWidth: 128,
  width: 128,
  position: "sticky",
  boxShadow: "2px 0 0 rgba(148, 163, 184, 0.18)",
  cursor: "default",
};

const nameHeaderCell = {
  ...headerBase,
  left: 128,
  zIndex: 9,
  minWidth: 250,
  width: 250,
  position: "sticky",
  boxShadow: "2px 0 0 rgba(148, 163, 184, 0.18)",
  cursor: "default",
};

const metricHeaderCell = {
  ...headerBase,
  textAlign: "right",
  minWidth: 132,
  width: 132,
};

const actionHeaderCell = {
  ...headerBase,
  textAlign: "center",
  minWidth: 78,
  width: 78,
  cursor: "default",
};

const cellBase = {
  padding: "8px 9px",
  fontSize: 12,
  color: "#1e293b",
  borderBottom: "1px solid #eef2f7",
  whiteSpace: "nowrap",
};

const tickerInputCell = {
  ...cellBase,
  position: "sticky",
  left: 0,
  zIndex: 4,
  background: "#ffffff",
  minWidth: 128,
  width: 128,
  boxShadow: "2px 0 0 rgba(226, 232, 240, 0.8)",
};

const nameCell = {
  ...cellBase,
  position: "sticky",
  left: 128,
  zIndex: 4,
  background: "#ffffff",
  minWidth: 250,
  width: 250,
  maxWidth: 250,
  overflow: "hidden",
  textOverflow: "ellipsis",
  boxShadow: "2px 0 0 rgba(226, 232, 240, 0.8)",
};

const metricCell = {
  ...cellBase,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  minWidth: 132,
  width: 132,
};

const actionCell = {
  ...cellBase,
  textAlign: "center",
  minWidth: 78,
  width: 78,
};

const tickerInput = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 950,
  color: "#0f172a",
  textTransform: "uppercase",
  outline: "none",
};

const tickerLink = {
  color: "#2563eb",
  textDecoration: "none",
  fontWeight: 950,
};

const companyName = {
  marginTop: 2,
  color: "#475569",
  fontSize: 11,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const mutedText = {
  color: "#94a3b8",
  fontSize: 12,
};

const missingText = {
  color: "#be123c",
  fontWeight: 850,
  fontSize: 12,
};

const removeButton = {
  width: 28,
  height: 28,
  borderRadius: 999,
  border: "1px solid #fecdd3",
  background: "#fff1f2",
  color: "#be123c",
  fontSize: 18,
  lineHeight: "20px",
  fontWeight: 900,
  cursor: "pointer",
};

const sortIndicator = {
  color: "#0f172a",
  fontSize: 10,
};

const rowEven = { background: "#ffffff" };
const rowOdd = { background: "#fbfdff" };

const noteBox = {
  marginTop: 12,
  padding: 12,
  borderRadius: 14,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
  fontSize: 12,
  lineHeight: 1.55,
};

const message = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 20,
  color: "#475569",
  background: "white",
  borderRadius: 16,
  border: "1px solid #e2e8f0",
};

const errorBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 14,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  borderRadius: 12,
};
